import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '@/lib/booking';

const AdminContext = createContext(null);

const ALL_SITES = [
  { key: 'all', label: 'All Sites', short: 'ALL' },
  { key: 'shihab_romeed', label: 'Shihab Romeed', short: 'SR' },
  { key: 'specialist_dental_london', label: 'Specialist Dental London', short: 'SDL' },
  { key: 'london_dental_academy', label: 'London Dental Academy', short: 'LDA' },
];

export function AdminProvider({ children }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(() => JSON.parse(localStorage.getItem('adminUser') || 'null'));
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  const token = localStorage.getItem('adminToken');
  const siteAccess = user?.siteAccess || ALL_SITES.filter(s => s.key !== 'all').map(s => s.key);
  const hasLDA = siteAccess.includes('london_dental_academy');

  // Auth check on mount
  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = localStorage.getItem('adminToken');
      if (storedToken && user) {
        setIsCheckingAuth(false);
        return;
      }
      try {
        const res = await fetch(`${API}/auth/refresh`, { method: 'POST', credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          localStorage.setItem('adminToken', data.accessToken);
          const userRes = await fetch(`${API}/auth/me`, { headers: { Authorization: `Bearer ${data.accessToken}` } });
          const userData = await userRes.json();
          localStorage.setItem('adminUser', JSON.stringify(userData));
          setUser(userData);
        } else {
          navigate('/admin/login');
        }
      } catch {
        navigate('/admin/login');
      } finally {
        setIsCheckingAuth(false);
      }
    };
    checkAuth();
  }, [navigate, user]);

  const logout = useCallback(async () => {
    try { await fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include' }); } catch {}
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    navigate('/admin/login');
  }, [navigate]);

  const hasAccess = useCallback((siteKey) => siteAccess.includes(siteKey), [siteAccess]);

  // Authed API fetch helper — auto-refreshes token on 401 and redirects to login if that fails
  const apiFetch = useCallback(async (path, options = {}) => {
    const makeRequest = (tkn, opts) => {
      const headers = { ...opts.headers, Authorization: `Bearer ${tkn}` };
      if (opts.body && typeof opts.body === 'object' && !(opts.body instanceof FormData)) {
        headers['Content-Type'] = 'application/json';
        opts = { ...opts, body: JSON.stringify(opts.body) };
      }
      return fetch(`${API}${path}`, { ...opts, headers });
    };

    const t = localStorage.getItem('adminToken');
    const res = await makeRequest(t, { ...options });

    if (res.status === 401) {
      // Attempt silent token refresh
      try {
        const refreshRes = await fetch(`${API}/auth/refresh`, { method: 'POST', credentials: 'include' });
        if (refreshRes.ok) {
          const data = await refreshRes.json();
          localStorage.setItem('adminToken', data.accessToken);
          // Retry original request with the new token
          return makeRequest(data.accessToken, { ...options });
        }
      } catch { /* refresh failed */ }

      // Refresh failed — force logout
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
      setUser(null);
      navigate('/admin/login');
      return res;
    }

    return res;
  }, [navigate]);

  if (isCheckingAuth || !user) return null;

  return (
    <AdminContext.Provider
      value={{
        user,
        token,
        siteAccess,
        hasAccess,
        hasLDA,
        logout,
        apiFetch,
        ALL_SITES,
      }}
    >
      {children}
    </AdminContext.Provider>
  );
}

export function useAdmin() {
  const ctx = useContext(AdminContext);
  if (!ctx) throw new Error('useAdmin must be used within AdminProvider');
  return ctx;
}

export default AdminContext;
